/**
 * Trailing time windows — the app's ONE vocabulary for "over what period".
 *
 * ## Why this is a leaf module
 *
 * Zero imports, by design. Constants that several surfaces share have been
 * bitten here before: putting one in `portfolio.ts` and importing it from
 * `user-settings.ts` produced Turbopack "export doesn't exist" errors while tsc
 * stayed green, because of the heavy import edge it created. A dependency-free
 * leaf is immune to that.
 *
 * ## Why one table for several surfaces
 *
 * The muscle map shipped `7D / 4W / 6M / 1Y / All` first. When Progress wanted
 * its own range picker the obvious move was a second list — which is exactly
 * how the dashboard heatmap ended up with a `60D` key labelled `2M`, a drift
 * its own comments still carry a warning about. One table, and each surface
 * declares the SUBSET it offers.
 *
 * The subsets genuinely differ, and for a reason rather than by accident:
 *
 *   - The muscle map measures ACUTE LOAD, so `7D` is its most useful window
 *     and its default.
 *   - Progress measures TREND. A week is noise there, and worse: a growth %
 *     needs `MIN_GROWTH_POINTS` (4) sessions, which almost nobody has for one
 *     lift inside 7 days — so offering 7D would blank the headline number on
 *     nearly every card. Progress starts at `4W` and adds `3M`, which the
 *     muscle map has no use for.
 *
 * Add a key here and neither surface changes until it opts in.
 */

/** Every window the app knows about, oldest-to-newest reading order. Key is
 *  the storage value; label is what the pill shows. They match 1:1 on purpose
 *  — see the drift note above. `days: Infinity` means all-time. */
export const TRAILING_WINDOWS = [
  { key: "7d", label: "7D", days: 7, caption: "Last 7 days" },
  { key: "4w", label: "4W", days: 28, caption: "Last 4 weeks" },
  { key: "3m", label: "3M", days: 90, caption: "Last 3 months" },
  { key: "6m", label: "6M", days: 180, caption: "Last 6 months" },
  { key: "1y", label: "1Y", days: 365, caption: "Last 12 months" },
  {
    key: "all",
    label: "All",
    days: Number.POSITIVE_INFINITY,
    caption: "All time",
  },
] as const;

export type TrailingWindow = (typeof TRAILING_WINDOWS)[number];
export type TrailingWindowKey = TrailingWindow["key"];

/** Look up a window by key. Returns undefined for an unknown key so callers
 *  can decide whether that's a coerce-to-default or a bug. */
export function trailingWindow(
  key: TrailingWindowKey,
): TrailingWindow | undefined {
  return TRAILING_WINDOWS.find((w) => w.key === key);
}

/**
 * Epoch-ms cutoff for a window, or null for all-time.
 *
 * Trailing from `now` rather than snapped to a calendar boundary: "last 4
 * weeks" should mean the same amount of training whichever day you open the
 * app, which is the same rolling model the muscle map already uses. A
 * calendar-snapped month would make early-month readings look like a slump
 * purely because less of the month had happened.
 *
 * Callers compare with `>=`, so a session exactly on the boundary is IN.
 */
export function trailingWindowStartMs(
  key: TrailingWindowKey,
  nowMs: number,
): number | null {
  const w = trailingWindow(key);
  if (w == null || !Number.isFinite(w.days)) return null;
  return nowMs - w.days * 24 * 60 * 60 * 1000;
}

/**
 * Build a surface's ordered subset.
 *
 * GENERIC on the key union so the literal keys survive. Callers derive their
 * own key type from the result (`(typeof X)[number]["key"]`), and a plain
 * `TrailingWindow[]` return would widen that to every window in the table —
 * which would let a `Record<SurfaceKey, T>` claim entries the surface never
 * builds, with the mismatch hidden behind the cast such records are built
 * with. The `as` below is the price of `.filter()` erasing that narrowing.
 *
 * Unknown keys are dropped rather than throwing: a surface naming a window
 * that no longer exists should lose a pill, not fail to render.
 */
export function trailingWindowSubset<K extends TrailingWindowKey>(
  keys: readonly K[],
): Extract<TrailingWindow, { key: K }>[] {
  return keys
    .map((k) => trailingWindow(k))
    .filter((w): w is TrailingWindow => w != null) as Extract<
    TrailingWindow,
    { key: K }
  >[];
}
